import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { mdiDelete, mdiDownload, mdiFlagCheckered, mdiPencil, mdiPlay, mdiRefresh } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import {
    downloadCampaignExport,
    useCampaign,
    useCloseCampaign,
    useDeleteCampaign,
    useLaunchCampaign,
    useRetryFailed,
    CampaignApiError,
    type Campaign,
} from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { OverflowMenu, OverflowMenuItem, OverflowMenuSeparator } from '../shared/OverflowMenu'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription } from '../ui/alert'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Skeleton } from '../ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { CAMPAIGN_STATUS } from './campaign-status'
import { CampaignChangesTab } from './CampaignChangesTab'
import { CampaignProgressTab } from './CampaignProgressTab'
import { CampaignSettingsTab } from './CampaignSettingsTab'
import { CampaignUnitsTab } from './CampaignUnitsTab'
import { formatDate } from './CampaignsPage'
import { StatusPill } from './StatusPill'

const MANAGER_TABS = ['units', 'progress', 'changes', 'settings'] as const

type Pending = 'launch' | 'close' | 'delete' | null

/**
 * One campaign (spec 2026-10-20-12 §7). A manager follows it (units, progress
 * by site and building, the values that contradict the patrimoine) and edits
 * it; an agent sees the units they survey. The tab lives in the URL.
 */
export function CampaignPage() {
    const { org, campaignUid } = useParams({ from: '/$org/campaigns/$campaignUid/' })
    const search = useSearch({ from: '/$org/campaigns/$campaignUid/' })
    const navigate = useNavigate()
    const { t } = useTranslation('campaigns')
    const { data: campaign, isLoading, error } = useCampaign(org, campaignUid)
    useBreadcrumbs([{ title: t('title'), path: `/${org}/campaigns` }, { title: campaign?.name ?? '' }])

    if (isLoading) return <Skeleton className="h-64 w-full" />
    if (error || !campaign) {
        return (
            <Alert variant="destructive" data-testid="campaign-error">
                <AlertDescription>{t('not_found')}</AlertDescription>
            </Alert>
        )
    }
    const tab = campaign.can_manage && MANAGER_TABS.includes(search.tab as (typeof MANAGER_TABS)[number])
        ? (search.tab as string)
        : campaign.status === 'draft' && campaign.can_manage
          ? 'settings'
          : 'units'

    return (
        <div className="space-y-6" data-testid="campaign-page">
            <PageHeader
                icon={SECTION_ICONS.campaigns}
                title={campaign.name}
                titleAside={<StatusPill map={CAMPAIGN_STATUS} family="status" value={campaign.status} testId="campaign-status" />}
                description={[
                    t(`level.${campaign.level}`),
                    campaign.due_on ? t('due_on', { date: formatDate(campaign.due_on) }) : undefined,
                    campaign.data_year ? t('data_year_value', { year: campaign.data_year }) : undefined,
                ].filter(Boolean).join(' · ')}
                actions={campaign.can_manage ? <ManagerActions org={org} campaign={campaign} /> : undefined}
            />
            {campaign.can_manage ? (
                <Tabs
                    value={tab}
                    onValueChange={(next) =>
                        navigate({ to: '/$org/campaigns/$campaignUid', params: { org, campaignUid }, search: { tab: next } })
                    }
                >
                    <TabsList className="flex-wrap">
                        {MANAGER_TABS.map((id) => (
                            <TabsTrigger key={id} value={id} data-testid={`campaign-tab-${id}`}>
                                {t(`tabs.${id}`)}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <TabsContent value="units">
                        <CampaignUnitsTab org={org} campaign={campaign} />
                    </TabsContent>
                    <TabsContent value="progress">
                        <CampaignProgressTab org={org} campaign={campaign} />
                    </TabsContent>
                    <TabsContent value="changes">
                        <CampaignChangesTab org={org} campaign={campaign} />
                    </TabsContent>
                    <TabsContent value="settings">
                        <CampaignSettingsTab org={org} campaign={campaign} />
                    </TabsContent>
                </Tabs>
            ) : (
                <CampaignUnitsTab org={org} campaign={campaign} />
            )}
        </div>
    )
}

const LAUNCH_FIELDS = ['scope_asset_uids', 'groupings', 'agent_uids']

function ManagerActions({ org, campaign }: { org: string; campaign: Campaign }) {
    const { t } = useTranslation('campaigns')
    const navigate = useNavigate()
    const launch = useLaunchCampaign(org, campaign.uid)
    const close = useCloseCampaign(org, campaign.uid)
    const remove = useDeleteCampaign(org, campaign.uid)
    const retry = useRetryFailed(org, campaign.uid)
    const [pending, setPending] = useState<Pending>(null)
    const [error, setError] = useState<string | null>(null)

    const run = async () => {
        setError(null)
        try {
            if (pending === 'launch') await launch.mutateAsync()
            if (pending === 'close') await close.mutateAsync()
            if (pending === 'delete') {
                await remove.mutateAsync()
                await navigate({ to: '/$org/campaigns', params: { org } })
            }
            setPending(null)
        } catch (e) {
            setError(launchError(e) ?? (e as Error).message)
        }
    }
    // A launch refused on a field (empty perimeter, no unit of the level, no
    // item, no agent) says which, in the user's language.
    const launchError = (e: unknown): string | null => {
        const field = pending === 'launch' && e instanceof CampaignApiError ? e.fields[0]?.name : undefined
        return field && LAUNCH_FIELDS.includes(field) ? t(`launch_errors.${field}`) : null
    }
    const exportIt = () => void downloadCampaignExport(org, campaign.uid, campaign.name).catch((e: Error) => setError(e.message))
    const primary = campaign.status === 'draft'
        ? { key: 'launch' as const, icon: mdiPlay, label: t('launch') }
        : campaign.status === 'launched'
          ? { key: 'close' as const, icon: mdiFlagCheckered, label: t('close') }
          : null

    return (
        <div className="flex items-center gap-2">
            {primary ? (
                <Button onClick={() => setPending(primary.key)} aria-label={primary.label} title={primary.label} data-testid={`campaign-${primary.key}-btn`}>
                    <AppIcon path={primary.icon} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{primary.label}</span>
                </Button>
            ) : (
                <Button onClick={exportIt} aria-label={t('export')} title={t('export')} data-testid="campaign-export-btn">
                    <AppIcon path={mdiDownload} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('export')}</span>
                </Button>
            )}
            <OverflowMenu testId="campaign-overflow" label={t('more_actions')}>
                {campaign.status === 'draft' && (
                    <OverflowMenuItem icon={mdiPencil} label={t('edit_questionnaire')} testId="campaign-edit-questionnaire">
                        <Link to="/$org/campaigns/$campaignUid/edit" params={{ org, campaignUid: campaign.uid }} />
                    </OverflowMenuItem>
                )}
                {campaign.status !== 'closed' && (
                    <OverflowMenuItem icon={mdiDownload} label={t('export')} onSelect={exportIt} testId="campaign-export-item" />
                )}
                {campaign.status === 'closed' && (
                    <OverflowMenuItem
                        icon={mdiRefresh}
                        label={t('retry_failed')}
                        onSelect={() => void retry.mutateAsync().catch((e: Error) => setError(e.message))}
                        testId="campaign-retry-item"
                    />
                )}
                {campaign.status === 'draft' && (
                    <>
                        <OverflowMenuSeparator />
                        <OverflowMenuItem icon={mdiDelete} label={t('delete')} destructive onSelect={() => setPending('delete')} testId="campaign-delete-item" />
                    </>
                )}
            </OverflowMenu>
            <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
                <AlertDialogContent data-testid="campaign-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{pending ? t(`confirm.${pending}_title`) : ''}</AlertDialogTitle>
                        <AlertDialogDescription>{pending ? t(`confirm.${pending}_description`) : ''}</AlertDialogDescription>
                    </AlertDialogHeader>
                    {error && <p className="text-sm text-destructive" role="alert" data-testid="campaign-confirm-error">{error}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="campaign-confirm-cancel">{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault()
                                void run()
                            }}
                            data-testid="campaign-confirm-ok"
                        >
                            {pending ? t(pending === 'delete' ? 'delete' : pending) : ''}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {error && pending === null && <span className="text-sm text-destructive" role="alert">{error}</span>}
        </div>
    )
}
